import { useState } from 'react';

// Components
import { ConfigContext, HeaderV2, RoundEvents, TextBlockTailwind } from '@velinsi/smp-widgets-ui';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';

// Hooks
import { useCompetitionSeasons } from '../../hooks/seasons/useCompetitionSeasons';
import { useTabsController } from '../../../../../packages/hooks/useTabsController';
import { useViewScrollDetector } from '../../../../../packages/hooks/useViewScrollDetector';
import { useTournamentProgramme } from '../../../../../packages/hooks/multi-sport/useTournamentProgramme';

// Utils
import { defaultHandleTournamentClick, defaultHandleUserInteractionClick } from '@sportal365/smp-widget-utils';

// Constants
import { FIXTURES } from '../../../../../packages/constants/programmeStatus';
import { DROPDOWN_SELECTION_SUBSCRIBE_TYPE, TAB_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';

// Types
import { BaseWidgetProps } from '../../../../../packages/types';
import { IMultiSportTournamentProgrammeConfig, IRoundFilter } from '../../../../../packages/types/multisport';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';

export const TournamentProgrammeWidget = ({
  themes,
  config,
  subscribers,
  shadow,
  sdkOptions,
  cuid,
}: BaseWidgetProps<IMultiSportTournamentProgrammeConfig>) => {
  const [hideBoxShadow, setHideBoxShadow] = useState(true);
  const [stageId, setStageId] = useState<string | undefined>(config?.dataStage);
  const [roundFilter, setRoundFilter] = useState<IRoundFilter[] | undefined>(config?.dataRoundsFilter);
  const [selectedPillTab, setSelectedPillTab] = useState(config?.dataHeaderDefaultOption?.toUpperCase() || FIXTURES);

  const { competition, seasonId, setSeasonId, seasonOptions } = useCompetitionSeasons({
    competitionId: config?.dataCompetition,
    sdkOptions,
    dataSeason: config?.dataSeason,
    dataEntityLinks: config?.dataEntityLinks,
  });

  const {
    widgetData: { games, isLoading, hasFixtures, hasResults },
    handleClick,
  } = useTournamentProgramme({
    subscribers,
    ...config,
    filter: selectedPillTab,
    dataSeason: seasonId,
    dataStage: stageId,
    dataRoundsFilter: roundFilter,
    dataWidgetCuid: cuid,
    sdkOptions,
  });

  const { pillTabOptions } = useTabsController({
    selectedPillTab,
    setSelectedPillTab,
    isFetching: isLoading,
    hasFixtures: hasFixtures,
    hasResults: hasResults,
    dataLabels: config?.dataLabels,
  });

  const { ref } = useViewScrollDetector({ setIsViewTop: setHideBoxShadow });

  const handleSeasonChange = (value?: string) => {
    setStageId('');
    setRoundFilter(undefined);
    setSeasonId(value);
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, DROPDOWN_SELECTION_SUBSCRIBE_TYPE);
  };

  const handlePillTabChange = (value: string) => {
    setSelectedPillTab(value);
    if (value !== selectedPillTab && ref.current) {
      ref.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, TAB_SELECTION_SUBSCRIBE_TYPE);
  };

  const handleCompetitionClick = () => defaultHandleTournamentClick(handleClick, competition?.id || '', competition?.title);

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <TailwindTheme
        isLoading={isLoading}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-primaryBackgroundColor)'
      >
        <HeaderV2
          {...competition}
          selectedSeason={seasonId}
          selectedPillTab={selectedPillTab}
          seasonOptions={seasonOptions}
          pillTabOptions={pillTabOptions}
          displayPillTabs={config?.dataOptionsDisplay}
          displaySeasonDropdown={config?.dataHeaderDisplay}
          hideBoxShadow={hideBoxShadow}
          withStickyHeader
          handleHeaderClick={handleCompetitionClick}
          handlePillTabChange={handlePillTabChange}
          handleSeasonChange={handleSeasonChange}
        />
        {games && games.length > 0 ? (
          games.flatMap(({ competition, stages }) =>
            stages.map(({ stage, rounds }, index) => (
              <RoundEvents
                key={index}
                stage={stage}
                rounds={rounds}
                standingsLabel={competition.standingsLabel}
                standingsUrl={competition.standingsUrl}
                handleClick={handleClick}
              />
            )),
          )
        ) : (
          <TextBlockTailwind text={config?.dataLabels?.no_games} />
        )}
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
